/**
 * Чтение/запись серверных настроек карты (`/api/v1/users/me/map-preferences`).
 *
 * Вынесено из `pages/map/v2.tsx` (AK-629), чтобы поведение при отказе ручки
 * проверялось тестами, а не глазами на staging.
 *
 * Что важно знать про эту ручку
 * -----------------------------
 * Настройки лежат в `auth.tenant_members.map_prefs` — то есть привязаны к
 * СТРОКЕ ЧЛЕНСТВА (user × tenant), а не к пользователю.
 *
 *   * GET никогда не падает 404: бэкенд отдаёт дефолты, если строки нет
 *     (`users.py:get_my_map_preferences`).
 *   * PATCH отдаёт **404 «Нет активного членства в тенанте»**, когда строки
 *     членства нет — а её нет, например, у консультанта в окне клиента
 *     (доступ там выдан грантом `auth.grants`, членство не создаётся).
 *
 * Раньше PATCH уходил на каждое изменение store (debounce 1с) и получал 404
 * снова и снова — консоль забивалась одинаковыми ошибками, а записать всё
 * равно было некуда. Теперь первый 404 гасит запись до конца загрузки
 * страницы: молча, без ретраев, без Sentry-шума. Настройки при этом живут в
 * localStorage (zustand persist), так что для пользователя ничего не теряется.
 *
 * Серверный upsert (создавать строку/хранить настройки для грантового
 * доступа) — отдельная задача бэкенда AK-629; когда он приедет, эта заглушка
 * просто перестанет срабатывать.
 */
import { captureException } from '@/lib/sentry';
import { fetchMapPreferences, invalidateMapPreferences } from '@/lib/http/sharedQueries';
import { toWire, fromWire } from '@/lib/map/state/mapPrefsWire';
import type { MapState } from '@/lib/map/state/mapState';

export type PersistPrefsOutcome =
  /** PATCH прошёл, общий кэш чтения инвалидирован. */
  | 'written'
  /** Записывать некуда (404 на этой загрузке страницы) — запрос не отправляли. */
  | 'skipped'
  /** Именно этот ответ был 404 — дальше не пишем. */
  | 'disabled'
  /** Сеть/5xx — молча пропускаем, ретраев нет (настройки не критичны). */
  | 'failed';

/**
 * Есть ли куда писать. Живёт в модуле, а не в store: это свойство сессии
 * (наличие строки членства), а не состояния карты.
 */
let prefsWritable = true;

/** Только для тестов — сбросить защёлку между кейсами. */
export function __resetMapPrefsWritable(): void {
  prefsWritable = true;
}

export function isMapPrefsWritable(): boolean {
  return prefsWritable;
}

/** `PATCH /users/me/map-preferences`. Никогда не reject'ит и не ретраит. */
export async function persistMapPrefs(snapshot: MapState): Promise<PersistPrefsOutcome> {
  if (!prefsWritable) return 'skipped';

  let response: Response;
  try {
    response = await fetch('/api/v1/users/me/map-preferences', {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(toWire(snapshot)),
    });
  } catch {
    // Оффлайн/обрыв. Настройки уже сохранены локально — молчим.
    return 'failed';
  }

  if (response.status === 404) {
    // Нет строки членства → писать некуда. Гасим до конца загрузки страницы,
    // чтобы не повторять 404 на каждое движение карты.
    prefsWritable = false;
    return 'disabled';
  }

  if (!response.ok) return 'failed';

  // Мы только что переписали настройки — общий кэш чтения протух.
  invalidateMapPreferences();
  return 'written';
}

/**
 * `GET /users/me/map-preferences` → срез `MapState`.
 *
 * Контракт, на который опирается hydrate карты: **никогда не reject'ит**.
 * Hydrate тянет настройки и `/scope/available` одним `Promise.all`, и падение
 * настроек не должно уносить с собой выбор стартового скоупа (иначе карта
 * остаётся в company-скоупе и прячет кнопку «Снимки» — лок AK-106).
 */
export async function loadMapPrefs(): Promise<Partial<MapState>> {
  // Перф-аудит 14.08 (корень 6): ×60 идентичных GET'ов за сессию. Настройки
  // карты правит только этот же клиент (PATCH выше инвалидирует кэш), поэтому
  // читать их чаще раза в 10 минут незачем.
  try {
    const body = await fetchMapPreferences<unknown>();
    if (body == null) return {};
    return fromWire(body as Parameters<typeof fromWire>[0]);
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[loadMapPrefs] fetch failed', err);
    else
      captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: { source: 'mapV2.loadPrefs' },
      });
    return {};
  }
}
